import { useInjectReducer } from "utils/@reduxjs/useInjectReducer";
import { companiesSaga } from "./saga";
import { useInjectSaga } from "utils/@reduxjs/useInjectSaga";
import { createSlice } from "@reduxjs/toolkit";
import { useMemo } from "react";

export const initialState = {
  companies: {
    initialLoading: true,
    loading: false,
    error: null,
    total_companies: 0,
    verified_companies: 0,
    unverified_companies: 0,
    no_status_companies: 0,
    companies: [],
    filtered_count: 0,
    pagination: {},
    status: "",
    search: "",
    search_field: "",
    sort_by: "",
    sort_order: ""
  },
  marketersList: {
    initialLoading: true,
    loading: false,
    error: null,
    company_id: "",
    company_name: "",
    marketers: [],
    pagination: {},
    total_marketers: ""
  },
  infoDetails: {
    loading: false,
    error: null,
    size: [],
    status: [],
    cities: [],
    types: [],
    specialized_in: [],
    specialized_regions: [],
    services: [],
    industries: [],
    domains: []
  }
};

export const CompaniesSlice = createSlice({
  name: "companies",
  initialState,
  reducers: {
    // --- Companies ---
    fetchCompanies: (state, action) => {
      const payload = action.payload || {}; // ensure it's at least {}
      state.companies.loading = true;
      if (state.companies.initialLoading) {
        state.companies.initialLoading = true;
      }
      state.companies.error = null;
      if (payload.status) state.companies.status = payload.status;
      if (payload.search) state.companies.search = payload.search;
      if (payload.search_field)
        state.companies.search_field = payload.search_field;
      if (payload.sort_by) state.companies.sort_by = payload.sort_by;
      if (payload.sort_order) state.companies.sort_order = payload.sort_order;
    },

    fetchCompaniesSuccess: (state, action) => {
      const {
        total_companies,
        verified_companies,
        unverified_companies,
        no_status_companies,
        companies,
        filtered_count,
        pagination
      } = action.payload;
      state.companies.loading = false;
      state.companies.initialLoading = false;
      state.companies.error = null;
      state.companies.total_companies = total_companies;
      state.companies.verified_companies = verified_companies;
      state.companies.unverified_companies = unverified_companies;
      state.companies.no_status_companies = no_status_companies;
      state.companies.companies = companies;
      state.companies.filtered_count = filtered_count;
      state.companies.pagination = pagination;
    },
    fetchCompaniesFailure: (state, action) => {
      state.companies.loading = false;
      state.companies.initialLoading = false; // stop initial loading
      state.companies.error = action.payload;
    },

    // --- Marketers ---
    fetchMarketers: (state, action) => {
      state.marketersList.loading = true;
      state.marketersList.error = null;

      if (action.payload?.companyId) {
        state.marketersList.company_id = action.payload.companyId || "";
      }
    },
    fetchMarketersSuccess: (state, action) => {
      const {
        marketers,
        company_id,
        company_name,
        pagination,
        total_marketers
      } = action.payload;
      state.marketersList.loading = false;
      state.marketersList.initialLoading = false;
      state.marketersList.marketers = marketers || [];
      state.marketersList.company_id = company_id || "";
      state.marketersList.company_name = company_name || "";
      state.marketersList.error = null;
      state.marketersList.pagination = pagination;
      state.marketersList.total_marketers = total_marketers;
    },
    fetchMarketersFailure: (state, action) => {
      state.marketersList.loading = false;
      state.marketersList.initialLoading = false;
      state.marketersList.error = action.payload;
    },

    fetchCompanyInfoDetails: (state) => {
      state.infoDetails.loading = true;
      state.infoDetails.error = null;
    },
    fetchCompanyInfoDetailsSuccess: (state, action) => {
      state.infoDetails.loading = false;
      state.infoDetails.error = null;
      state.infoDetails = { ...state.infoDetails, ...action.payload };
    },
    fetchCompanyInfoDetailsFailure: (state, action) => {
      state.infoDetails.loading = false;
      state.infoDetails.error = action.payload;
    },

    // --- Update Company Info Details ---
    updateCompanyDetails: (state) => {
      state.infoDetails.loading = true;
      state.infoDetails.error = null;
    },
    updateCompanyDetailsSuccess: (state, action) => {
      state.infoDetails.loading = false;
      state.infoDetails.error = null;

      const updatedCompany = action.payload;

      const index = state.companies.companies.findIndex(
        (c) => c?.id === updatedCompany?.id
      );

      if (index !== -1) {
        state.companies.companies[index] = {
          ...state.companies.companies[index],
          ...updatedCompany
        };
      }

      // Also update infoDetails state
      state.infoDetails = { ...state.infoDetails, ...updatedCompany };
    },
    updateCompanyDetailsFailure: (state, action) => {
      state.infoDetails.loading = false;
      state.infoDetails.error = action.payload;
    }
  }
});

export const useCompaniesSlice = () => {
  useInjectReducer(CompaniesSlice.name, CompaniesSlice.reducer);
  useInjectSaga(CompaniesSlice.name, companiesSaga);
  const actions = useMemo(() => CompaniesSlice.actions, []);
  return { actions };
};

export const { actions: companiesActions } = CompaniesSlice;
export default CompaniesSlice.reducer;
